import { OverlayModule } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { prefersReducedMotion, provideMotionAwareAnimations } from '@infinitform/shared-ui';
import { Chart } from 'chart.js';
import { provideRouter } from '@angular/router';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { configureEditorEnvironment, EDITOR_ENVIRONMENT } from '@infinitform/editor-features';
import { BDF_SERVICE, SOFTJAWS_SERVICE, USER_SETTINGS_SERVICE, AUTO_ORIENT_SERVICE, SHARP_EDGES_SERVICE, FILE_SAVE_SERVICE, RUN_RATING_SERVICE } from '@infinitform/platform-core';
import { CloudBdfService, CloudSoftjawsService, UserSettingsCloudService, CLOUD_PLATFORM_CONFIG, CloudAutoOrientService, CloudSharpEdgesService, CloudRunRatingService, FileSaveCloudService } from '@infinitform/platform-cloud';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';
import { AuthInterceptor } from './app/core/interceptors/auth.interceptor';
import { AppInitializationService } from './app/core/services/app-initialization.service';

// Charts draw without animation under reduced motion.
if (prefersReducedMotion()) {
  Chart.defaults.set({ animation: false });
}

// Configure editor-features static environment before bootstrap
const editorEnvConfig = {
  production: environment.PRODUCTION,
  apiAddress: environment.API_ADDRESS,
  socketAddress: environment.SOCKET_ADDRESS,
  frontendAddress: environment.FRONTEND_ADDRESS,
  editorAddress: environment.EDITOR_ADDRESS,
  subscriptionsEnabled: environment.SUBSCRIPTIONS_ENABLED,
  subscriptionDebug: (environment as any).SUBSCRIPTION_DEBUG ?? false,
  cadEditor: environment.CAD_EDITOR,
  cadAdminOnly: (environment as any).CAD_ADMIN_ONLY ?? false,
  apiStorageMode: (environment as any).API_STORAGE_MODE as 's3' | 'local' ?? 's3',
  envLabel: environment.ENV_LABEL,
};

configureEditorEnvironment(editorEnvConfig);

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(APP_ROUTES),
    importProvidersFrom(BrowserModule, FormsModule, OverlayModule),
    provideHttpClient(withInterceptorsFromDi()),
    provideMotionAwareAnimations(),
    {
      provide: EDITOR_ENVIRONMENT,
      useValue: editorEnvConfig,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    provideAppInitializer(() => inject(AppInitializationService).initializeApp()),
    provideCharts(withDefaultRegisterables()),
    { provide: CLOUD_PLATFORM_CONFIG, useValue: { apiBaseUrl: environment.API_ADDRESS } },
    { provide: BDF_SERVICE, useClass: CloudBdfService },
    { provide: SOFTJAWS_SERVICE, useClass: CloudSoftjawsService },
    { provide: USER_SETTINGS_SERVICE, useClass: UserSettingsCloudService },
    { provide: AUTO_ORIENT_SERVICE, useClass: CloudAutoOrientService },
    { provide: SHARP_EDGES_SERVICE, useClass: CloudSharpEdgesService },
    { provide: RUN_RATING_SERVICE, useClass: CloudRunRatingService },
    { provide: FILE_SAVE_SERVICE, useClass: FileSaveCloudService },
  ]
}).catch((err) => console.error(err));
